/**
 * The bridge between a stored `Character` and the active game system.
 *
 * `Character` is the app's shape: long ability keys, `systemState` as an opaque
 * bag. `SystemCharacter` is the adapter's shape: short ability ids, everything
 * system-specific in `systemData`. Every component that wants to ask the active
 * system a question about a character goes through here, so the translation
 * exists once and no component ends up branching on a system id.
 *
 * `systemState` is handed across verbatim. The adapter owns the decoding of its
 * own slot — the web layer must not learn what is inside it, or the whole point
 * of the adapter is lost.
 */

import { armorClassOf, classesOf, creationSpecFor, getActiveSystem, hasClasses, sheetSectionsFor, toSystemAbilities, totalLevel, type AbilityCreationSpec, type Character, type SheetSection, type SystemCharacter } from '@machina/core';

/** The active system's creation spec — abilities to prompt for and values to hand out. */
export function activeCreationSpec(): AbilityCreationSpec {
  return creationSpecFor(getActiveSystem());
}

/** Ability definitions the active system uses, in its own order. */
export function activeAbilities() {
  return activeCreationSpec().abilities;
}

/**
 * Put a stored character into the envelope the adapter expects.
 *
 * Abilities are narrowed to the ones the active system declares, so a 5e
 * character opened under 5e is unchanged, and a character carrying an
 * Attunement score in a system that has no such ability simply does not present
 * one to the rules engine.
 */
export function toSystemCharacter(character: Character): SystemCharacter {
  const adapter = getActiveSystem();

  return {
    id: character.id,
    name: character.name,
    systemId: adapter?.id ?? 'dnd-5e',
    // `abilityScores` is a plain record now, so the cast this used to need is
    // gone along with the six declared fields it was working around.
    abilities: toSystemAbilities(character.abilityScores, adapter?.getAbilities() ?? []),
    // Undefined, not 0, when the system has no classes: a Trophy character is
    // not level 0, it is a character in a game without levels.
    level: hasClasses(character) ? totalLevel(character) : undefined,
    classes: hasClasses(character)
      ? classesOf(character).map(c => ({
          classId: c.name,
          level: c.level,
          subclass: c.subclass,
        }))
      : undefined,
    health: { current: character.currentHp, max: character.maxHp, temp: character.tempHp },
    defense: armorClassOf(character),
    speed: character.speed,
    // `Character` has no conditions field — they live on the token/combatant,
    // not the sheet. An empty list is the honest answer, not a gap to paper over.
    conditions: [],
    systemData: character.systemState,
  };
}

/** Sections the active system contributes to this character's sheet. */
export function systemSheetSections(character: Character): SheetSection[] {
  return sheetSectionsFor(getActiveSystem(), toSystemCharacter(character));
}
